import type { MergeShape } from "./mergePlan";

interface ClipSize { width: number; height: number }

const SHAPE_SIZE: Record<Exclude<MergeShape, "first">, [number, number]> = { landscape: [1920, 1080], portrait: [1080, 1920], square: [1080, 1080] };

/**
 * Said before merging: which clips differ from the output picture size and what will happen to them.
 * (Frame rate is not known until the file is read by the video engine, so it is reported after the merge.)
 */
export function mismatchNote(sizes: (ClipSize | null)[], shape: MergeShape, fill: boolean): string | null {
  const known = sizes.filter((s): s is ClipSize => !!s);
  if (known.length < 2) return null;
  const [w, h] = shape === "first" ? [known[0].width, known[0].height] : SHAPE_SIZE[shape];
  const odd = known.filter((s) => s.width !== w || s.height !== h);
  if (!odd.length) return null;
  const how = fill ? "scaled up and cropped at the edges" : "scaled to fit with black bars";
  const distinct = Array.from(new Set(odd.map((s) => `${s.width}×${s.height}`)));
  return `${odd.length === 1 ? "One clip is" : `${odd.length} clips are`} not ${w}×${h} (${distinct.slice(0, 3).join(", ")}${distinct.length > 3 ? ", ..." : ""}). ${odd.length === 1 ? "It" : "They"} will be ${how}, never stretched, and the whole video is re-encoded; frame rates are matched too.`;
}

interface ResultInfo { mode: "copy" | "reencode"; fitted: boolean; width: number; height: number; fps: number; fpsMismatch: boolean; crossfade: number }

/** The sentence fragment appended to the "Videos merged" message. */
export function mergeResultNote(out: ResultInfo, fill: boolean): string {
  const parts: string[] = [];
  if (out.mode === "copy") return " (joined without re-encoding - no quality loss)";
  if (out.fitted) parts.push(`clips of different sizes were fitted to ${out.width} x ${out.height} ${fill ? "filling the frame, with the edges cropped, never stretched" : "with black bars, never stretched"}`);
  else parts.push(`re-encoded at ${out.width} x ${out.height}`);
  if (out.fpsMismatch) parts.push(`clips with a different frame rate were converted to ${out.fps} fps`);
  if (out.crossfade) parts.push(`${out.crossfade} s crossfade at each join`);
  return ` (${parts.join("; ")})`;
}
